import {
  errorMessage,
  Timezone,
  updateApplicantResumeProfileRoute,
  updateEmployerProfileRoute,
} from '@features/profile';
import axiosInstance from '@server/axiosInstance';
import { authContext, profileKey } from '@staffedupusa/shared-components';
import { updateWalkthroughStatus } from '@utils/updateOnboardingStatus';
import { Dispatch, SetStateAction, useContext } from 'react';
import { useMutation, useQueryClient } from 'react-query';

import { useUpdateStatus } from './useUpdateStatus';

type UseUpdateProfileProps = {
  setErrorText?: Dispatch<SetStateAction<string>>;
  onSuccess?: () => void;
};

type CommonFields = {
  timezone?: Timezone;
};

type WhyWeWorkBlock = {
  f_icon?: string | null;
  heading?: string;
  description?: string;
};

type EmployerFields = {
  walkthrough_completed?: boolean;
  default_request_more_info_message?: string;
  default_open_interview_message?: string;
  default_interview_message?: string;
  default_saved_message?: string;
  default_declined_message?: string;
  default_hire_message?: string;
  default_archive_message?: string;
  keep_declined_applications?: number | 'Never';
  keep_unattended_applications?: number | 'Never';
  unresponsive_message?: number | 'Never';
  unresponsive_interview?: number | 'Never';
  mon_availability?: number;
  tues_availability?: number;
  wed_availability?: number;
  thurs_availability?: number;
  fri_availability?: number;
  sat_availability?: number;
  sun_availability?: number;
  s_name?: string;
  s_email?: string;
  s_phone?: string;
  s_slug?: string;
  has_payment_method?: boolean;
  has_visited_job_posting_page?: boolean;
  has_added_pre_qualification_questions?: boolean;
  has_visited_qualification_question_page?: boolean;
  has_more_locations_answered?: boolean;
  job_offer_manual?: boolean;
  has_hired_model_shown?: number;
  hide_jobs_closed_suggestion?: number;
  auto_clear_notifications?: number;
  physical_interview_address?: string | boolean;
  physical_interview_location?: string | null;
  virtual_interview_link?: string | null;
  hire_flix_location?: boolean | string;
  why_we_work?: {
    heading: string;
    blocks: WhyWeWorkBlock[];
  };
};

type ApplicantFields = {
  employment_status?: string;
};

export const useUpdateProfile = ({
  setErrorText = () => {},
  onSuccess = () => {},
}: UseUpdateProfileProps) => {
  const {
    authData: { user },
  } = useContext(authContext);

  const client = useQueryClient();
  const { updateStatus } = useUpdateStatus({});

  const { mutate: updateProfile, isLoading: isUpdatingProfile } = useMutation({
    mutationFn: (variables: CommonFields | EmployerFields | ApplicantFields) => {
      // Decide headers dynamically
      const isEmployer = Boolean(user?.company);
      const hasWhyWeWork =
        isEmployer && (variables as EmployerFields)?.why_we_work !== undefined;

      const headers = {
        'Content-Type': hasWhyWeWork
          ? 'application/json'
          : 'multipart/form-data',
      };

      return axiosInstance
        .post(
          isEmployer
            ? updateEmployerProfileRoute
            : updateApplicantResumeProfileRoute,
          variables,
          { headers },
        )
        .then((res) => res.data);
    },
    onSuccess: (data) => {
      if (data.status !== 200) {
        setErrorText(data.message);
      } else {
        setErrorText('');
        client.invalidateQueries([profileKey]);

        if (
          data?.object?.company &&
          data?.object.company.auto_interview_invite &&
          data?.object.company.auto_save_applications
        ) {
          updateWalkthroughStatus(
            data?.object?.company.plan,
            data?.object,
            undefined,
            updateStatus,
          );
        }

        onSuccess?.();
      }
    },
    onError: () => {
      setErrorText(errorMessage);
    },
  });

  return {
    updateProfile,
    isUpdatingProfile,
  };
};
